import React, { useState, Fragment, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Input,
  Label,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFolder } from "api/mutators/file";
import { useTranslation } from "react-i18next";

const defaultValues = {
  name: "",
  description: "",
  view: "ALL",
};

const FolderModal = (props: any) => {
  const { t } = useTranslation("translation", { keyPrefix: "FILE_MANAGER" });

  const schema = yup.object().shape({
    name: yup.string().required(t("ERRORS:TITLE")),
    description: yup.string(),
    view: yup.string().required(t("ERRORS:TITLE")),
  });

  const { modal, toggle, setModal, refetchFiles, folderId, folderView } = props;

  const {
    reset,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: createFolder,
    onError: (error: any) => {
      queryClient.invalidateQueries({
        queryKey: ["fetchMemoryItemsInFolderForTable"],
      });
      queryClient.invalidateQueries({ queryKey: ["fetchMemoryItemsInFolder"] });
      toast.error("Error! File not created", { autoClose: 3000 });
    },
    onSuccess: () => {
      setModal(false);
      reset(defaultValues);
      refetchFiles();
      queryClient.invalidateQueries({
        queryKey: ["fetchMemoryItemsInFolderForTable"],
      });
      queryClient.invalidateQueries({ queryKey: ["fetchMemoryItemsInFolder"] });
      toast.success("File Created Successfully", { autoClose: 3000 });
    },
  });

  function onSubmit() {
    mutation.mutate({ ...getValues(), parentFolderId: folderId });
  }

  useEffect(() => {
    if (folderView && folderView !== "ALL") {
      setValue("view", folderView);
    } else {
      setValue("view", "ALL");
    }
  }, [folderView, setValue]);

  return (
    <Fragment>
      <Modal id="showModal" size={"xl"} isOpen={modal} toggle={toggle} centered>
        <ModalHeader className="bg-light p-3" toggle={toggle}>
          {t("MODALS:ADD")}
        </ModalHeader>
        <Form className="tablelist-form" onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <>
              <Row>
                <Col lg={4}>
                  <div className="mb-3 pb-2">
                    <Label htmlFor="view" className="form-label">
                      {t("MODALS:VIEW")}
                    </Label>
                    <Controller
                      control={control}
                      name="view"
                      render={({ field }) => (
                        <select
                          {...field}
                          className="form-select"
                          id="view"
                          value={field.value || defaultValues.view}
                          onChange={(e) => field.onChange(e.target.value)}
                          disabled={folderView !== "ALL"}
                        >
                          <option value="ALL">{t("MODALS:ALL")}</option>
                          <option value="CAREGIVER_PROFESSIONAL">
                            {t("MODALS:CAREGIVER_PROFESSIONAL")}
                          </option>
                          <option value="CAREGIVER_USER">
                            {t("MODALS:CAREGIVER_USER")}
                          </option>
                        </select>
                      )}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="mb-3 pb-2">
                    <Label className="form-label" for="name">
                      {t("MODALS:NAME")}
                    </Label>
                    <Controller
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="name"
                          placeholder={t("MODALS:INSERT_TITLE")}
                          invalid={!!errors.name}
                        />
                      )}
                    />
                    {errors.name && (
                      <FormFeedback>{errors.name.message}</FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <div className="mb-3 pb-2">
                    <Label htmlFor="description" className="form-label">
                      {t("MODALS:DESCRIPTION")}
                    </Label>
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <textarea
                          {...field}
                          className="form-control"
                          id="description"
                          rows={3}
                        ></textarea>
                      )}
                    />
                  </div>
                </Col>
              </Row>
            </>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  reset(defaultValues);
                  setModal(false);
                }}
              >
                {t("MODALS:CLOSE")}
              </button>
              <button type="submit" className="btn btn-soft-danger">
                {t("MODALS:SAVE")}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
      <ToastContainer closeButton={false} limit={1} />
    </Fragment>
  );
};

export default FolderModal;
